import React, { useState, useEffect } from 'react';
import {
  CreditCard, Check, X, Zap, Crown, Sparkles,
  Loader, AlertCircle, ArrowRight, Shield, Clock,
  Target, TrendingUp, Activity
} from 'lucide-react';

const SubscriptionPage = ({ user, setShowAuth }) => {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [optionsProSubscription, setOptionsProSubscription] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [optionsBillingCycle, setOptionsBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [error, setError] = useState(null);
  const [eduLoading, setEduLoading] = useState(false);
  const [eduSuccess, setEduSuccess] = useState(false);
  const [eduError, setEduError] = useState(null);

  // Detect .edu email
  const isEduEmail = user?.email?.toLowerCase().endsWith('.edu') || user?.email?.toLowerCase().includes('.k12.');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  useEffect(() => {
    fetchPlans();
    if (user?.id) {
      fetchSubscriptionStatus();
    }
  }, [user]);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/stripe/plans`);
      const data = await response.json();
      if (data.success) {
        setPlans(data.plans);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/stripe/subscription-status/${user.id}`);
      const data = await response.json();
      if (data.success) {
        setCurrentSubscription(data.subscription);
        setOptionsProSubscription(data.optionsPro);  // 🆕 Get Options Pro status
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const handleSubscribe = async (plan, isOptionsPro = false) => {
    if (!user) {
      if (setShowAuth) { setShowAuth(true); } else { alert('Please log in to start your free trial'); }
      return;
    }

    const cycle = isOptionsPro ? optionsBillingCycle : billingCycle;
    const priceId = cycle === 'monthly' 
      ? plan.priceIds.monthly 
      : plan.priceIds.yearly;

    if (!priceId) return;

    setCheckoutLoading(plan.id);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          userId: user.id,
          userEmail: user.email,
          productType: isOptionsPro ? 'options_pro' : 'main'
        })
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('Error creating checkout:', err);
      setError('Failed to start checkout process');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!user?.id) return;

    try {
      const response = await fetch(`${API_URL}/stripe/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id })
      });

      const data = await response.json();

      if (data.success && data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to open billing portal');
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      setError('Failed to open billing portal');
    }
  };

  const handleEduAccess = async () => {
    if (!user) {
      if (setShowAuth) setShowAuth(true);
      return;
    }
    setEduLoading(true);
    setEduError(null);
    try {
      const res = await fetch(`${API_URL}/subscription/edu-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id }
      });
      const data = await res.json();
      if (data.success) {
        setEduSuccess(true);
        // Refresh subscription status
        await fetchSubscriptionStatus();
      } else {
        setEduError(data.error || 'Unable to grant student access');
      }
    } catch (err) {
      setEduError('Something went wrong. Please try again.');
    } finally {
      setEduLoading(false);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free': return <Zap size={24} />;
      case 'pro': return <Sparkles size={24} />;
      case 'premium': return <Crown size={24} />;
      case 'options_pro': return <Target size={24} />;
      default: return <Zap size={24} />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'free': return '#94a3b8';
      case 'pro': return '#10b981';
      case 'premium': return '#8b5cf6';
      case 'options_pro': return '#f59e0b';
      default: return '#94a3b8';
    }
  };

  // Filter main plans and options pro plan
  const mainPlans = plans.filter(p => !p.isAddon);
  const optionsProPlan = plans.find(p => p.id === 'options_pro');

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Loader size={32} className="spin" style={{ color: '#60a5fa' }} />
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: window.innerWidth < 768 ? '1rem' : '2rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          fontSize: window.innerWidth < 768 ? '1.75rem' : '2.5rem',
          fontWeight: 'bold',
          marginBottom: '0.75rem',
          background: 'linear-gradient(135deg, #60a5fa 0%, #8b5cf6 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Choose Your Plan
        </h1>
        <p style={{
          fontSize: '1.1rem',
          color: '#94a3b8',
          marginBottom: '1.5rem'
        }}>
          Start with a 7-day free trial. Cancel anytime.
        </p>

        {/* Billing Toggle */}
        <div style={{
          display: 'inline-flex',
          background: 'rgba(30, 41, 59, 0.5)',
          borderRadius: '0.5rem',
          padding: '0.25rem'
        }}>
          <button
            onClick={() => setBillingCycle('monthly')}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '0.375rem',
              border: 'none',
              background: billingCycle === 'monthly' ? '#3b82f6' : 'transparent',
              color: billingCycle === 'monthly' ? 'white' : '#94a3b8',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem',
              transition: 'all 0.2s'
            }}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            style={{
              padding: '0.5rem 1.25rem',
              borderRadius: '0.375rem',
              border: 'none',
              background: billingCycle === 'yearly' ? '#3b82f6' : 'transparent',
              color: billingCycle === 'yearly' ? 'white' : '#94a3b8',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem',
              transition: 'all 0.2s'
            }}
          >
            Yearly
            <span style={{
              marginLeft: '0.5rem',
              background: '#10b981',
              color: 'white',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.7rem'
            }}>
              Save 30%
            </span>
          </button>
        </div>
      </div>

      {/* Current Subscription Status */}
      {currentSubscription && currentSubscription.tier !== 'free' && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '0.75rem',
          padding: '1rem 1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
              <Shield size={18} color="#10b981" />
              <span style={{ fontWeight: '600', color: '#10b981' }}>
                {currentSubscription.tier.charAt(0).toUpperCase() + currentSubscription.tier.slice(1)} Plan
              </span>
              {currentSubscription.isTrialing && (
                <span style={{
                  background: '#f59e0b',
                  color: 'white',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.7rem'
                }}>
                  Trial
                </span>
              )}
            </div>
            <p style={{ fontSize: '0.875rem', color: '#94a3b8', margin: 0 }}>
              {currentSubscription.cancelAtPeriodEnd 
                ? `Cancels on ${new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}`
                : currentSubscription.isTrialing
                  ? `Trial ends ${new Date(currentSubscription.trialEnd).toLocaleDateString()}`
                  : `Renews ${new Date(currentSubscription.currentPeriodEnd).toLocaleDateString()}`
              }
            </p>
          </div>
          <button
            onClick={handleManageSubscription}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid #10b981',
              borderRadius: '0.5rem',
              color: '#10b981',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            Manage Subscription
          </button>
        </div>
      )}

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '0.5rem',
          padding: '1rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#fca5a5'
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Student Program — always visible to all users */}
      <div style={{
        marginBottom: '2rem',
        background: 'linear-gradient(135deg, rgba(16,185,129,0.14) 0%, rgba(6,182,212,0.1) 100%)',
        border: '1.5px solid rgba(16,185,129,0.4)',
        borderRadius: '1rem',
        padding: '1.25rem 1.75rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '1.25rem', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(16,185,129,0.15)',
            border: '1.5px solid rgba(16,185,129,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.4rem',
          }}>🎓</div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: '#f1f5f9' }}>Students get full access free for 3 months</span>
              <span style={{ fontSize: '0.62rem', background: 'rgba(16,185,129,0.2)', color: '#10b981', padding: '0.15rem 0.55rem', borderRadius: '999px', fontWeight: 700, border: '1px solid rgba(16,185,129,0.3)' }}>NO CREDIT CARD</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Sign up with your</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '0.1rem 0.5rem', borderRadius: '4px' }}>.edu or .k12 email</span>
              <span style={{ fontSize: '0.82rem', color: '#94a3b8' }}>and unlock</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '0.1rem 0.45rem', borderRadius: '4px', border: '1px solid rgba(251,191,36,0.2)' }}>Premium</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#fb923c', background: 'rgba(249,115,22,0.1)', padding: '0.1rem 0.45rem', borderRadius: '4px', border: '1px solid rgba(249,115,22,0.2)' }}>Options Pro</span>
              <span style={{ fontSize: '0.82rem', color: '#64748b' }}>— automatically.</span>
            </div>
          </div>
        </div>
        {!user ? (
          <button
            onClick={() => setShowAuth && setShowAuth(true)}
            style={{
              padding: '0.65rem 1.4rem', flexShrink: 0,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none', borderRadius: '8px', color: 'white',
              fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              boxShadow: '0 2px 12px rgba(16,185,129,0.3)',
            }}
          >
            🎓 Sign up with .edu
          </button>
        ) : !isEduEmail ? (
          <div style={{ fontSize: '0.75rem', color: '#334155', textAlign: 'right', flexShrink: 0 }}>
            Logged in as <strong style={{ color: '#475569' }}>{user?.email}</strong><br/>
            <span style={{ color: '#1e3a5f' }}>Not a .edu address</span>
          </div>
        ) : null}
      </div>

      {/* Conditional .edu activation banner — only for .edu users on free tier */}
      {isEduEmail && (!currentSubscription?.tier || currentSubscription?.tier === 'free') && (
        <div style={{
          marginBottom: '2rem',
          background: 'linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(6,182,212,0.08) 100%)',
          border: '1px solid rgba(16,185,129,0.35)',
          borderRadius: '1rem',
          padding: '1.5rem 2rem',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '1rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <div style={{ fontSize: '2rem', flexShrink: 0 }}>🎓</div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '1rem', fontWeight: 700, color: '#10b981' }}>Student Access — Free for 3 Months</span>
                <span style={{ fontSize: '0.65rem', background: 'rgba(16,185,129,0.15)', color: '#10b981', padding: '0.1rem 0.5rem', borderRadius: '999px', fontWeight: 700 }}>NO CREDIT CARD</span>
                <span style={{ fontSize: '0.65rem', background: 'rgba(251,191,36,0.15)', color: '#fbbf24', padding: '0.1rem 0.5rem', borderRadius: '999px', fontWeight: 700 }}>PREMIUM</span>
                <span style={{ fontSize: '0.65rem', background: 'rgba(249,115,22,0.15)', color: '#fb923c', padding: '0.1rem 0.5rem', borderRadius: '999px', fontWeight: 700 }}>OPTIONS PRO</span>
              </div>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0, lineHeight: 1.5 }}>
                We detected your student email — <strong style={{ color: '#f1f5f9' }}>{user?.email}</strong>.
                Get full <strong style={{ color: '#fbbf24' }}>Premium + Options Pro</strong> access free for 3 months. No credit card required.
              </p>
              {eduError && <p style={{ fontSize: '0.8rem', color: '#f87171', margin: '0.5rem 0 0' }}>{eduError}</p>}
              {eduSuccess && <p style={{ fontSize: '0.8rem', color: '#10b981', margin: '0.5rem 0 0' }}>✅ Activated! Enjoy 3 months of Premium + Options Pro access.</p>}
            </div>
          </div>
          <button
            onClick={handleEduAccess}
            disabled={eduLoading || eduSuccess}
            style={{
              padding: '0.75rem 1.75rem',
              background: eduSuccess ? '#1e293b' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              border: 'none', borderRadius: '0.5rem',
              color: 'white', fontSize: '0.9rem', fontWeight: 700,
              cursor: eduLoading || eduSuccess ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              flexShrink: 0, opacity: eduLoading ? 0.7 : 1,
            }}
          >
            {eduLoading ? <><Loader size={16} className="spin"/> Activating...</> :
             eduSuccess ? '✅ Activated!' :
             <><Zap size={16}/> Activate Student Access</>}
          </button>
        </div>
      )}

      {/* Main Pricing Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth < 768 
          ? '1fr' 
          : window.innerWidth < 1024 
            ? 'repeat(2, 1fr)' 
            : 'repeat(3, 1fr)',
        gap: '1.5rem',
        marginBottom: '3rem'
      }}>
        {mainPlans.map((plan) => {
          const isCurrentPlan = currentSubscription?.tier === plan.id;
          const price = billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly;
          const monthlyEquivalent = billingCycle === 'yearly' ? (plan.price.yearly / 12).toFixed(2) : null;

          return (
            <div
              key={plan.id}
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: plan.popular 
                  ? '2px solid #10b981' 
                  : isCurrentPlan 
                    ? '2px solid #3b82f6'
                    : '1px solid #334155',
                borderRadius: '1rem',
                padding: '1.5rem',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  padding: '0.25rem 1rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  Most Popular
                </div>
              )}

              {/* Current Plan Badge */}
              {isCurrentPlan && (
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  right: '1rem',
                  background: '#3b82f6',
                  color: 'white',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '9999px',
                  fontSize: '0.7rem',
                  fontWeight: '600'
                }}>
                  Current Plan
                </div>
              )}

              {/* Plan Header */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  color: getPlanColor(plan.id)
                }}>
                  {getPlanIcon(plan.id)}
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>
                    {plan.name}
                  </h3>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#94a3b8', margin: 0 }}>
                  {plan.description}
                </p>
              </div>

              {/* Price */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: '700', color: 'white' }}>
                    ${billingCycle === 'monthly' ? price : monthlyEquivalent}
                  </span>
                  <span style={{ color: '#94a3b8' }}>/month</span>
                </div>
                {billingCycle === 'yearly' && plan.price.yearly > 0 && (
                  <p style={{ fontSize: '0.875rem', color: '#10b981', margin: '0.25rem 0 0' }}>
                    ${plan.price.yearly} billed annually
                  </p>
                )}
                {plan.savings && billingCycle === 'yearly' && (
                  <p style={{ fontSize: '0.75rem', color: '#f59e0b', margin: '0.25rem 0 0' }}>
                    {plan.savings}
                  </p>
                )}
              </div>

              {/* Features */}
              <div style={{ flex: 1, marginBottom: '1.5rem' }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {plan.features.map((feature, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.875rem',
                      color: '#e2e8f0'
                    }}>
                      <Check size={16} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                      {feature}
                    </li>
                  ))}
                  {plan.limitations && plan.limitations.map((limitation, idx) => (
                    <li key={`limit-${idx}`} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                      fontSize: '0.875rem',
                      color: '#64748b'
                    }}>
                      <X size={16} color="#64748b" style={{ flexShrink: 0, marginTop: '2px' }} />
                      {limitation}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handleSubscribe(plan)}
                disabled={isCurrentPlan || checkoutLoading === plan.id || plan.id === 'free'}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: isCurrentPlan
                    ? '#475569'
                    : plan.id === 'free'
                      ? 'transparent'
                      : plan.popular
                        ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: plan.id === 'free' ? '#94a3b8' : 'white',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: isCurrentPlan || plan.id === 'free' ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  border: plan.id === 'free' ? '1px solid #475569' : 'none'
                }}
              >
                {checkoutLoading === plan.id ? (
                  <>
                    <Loader size={18} className="spin" />
                    Processing...
                  </>
                ) : isCurrentPlan ? (
                  'Current Plan'
                ) : plan.id === 'free' ? (
                  'Free Forever'
                ) : (
                  <>
                    Start 7-Day Free Trial
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* 🆕 OPTIONS PRO ADD-ON SECTION */}
      {optionsProPlan && (
        <div style={{ marginBottom: '3rem' }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem'
          }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}>
              <Target size={28} color="#f59e0b" />
              Options Trading Add-On
            </h2>
            <p style={{ color: '#94a3b8', fontSize: '1rem' }}>
              Professional options trading tools - Subscribe separately
            </p>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
            border: '2px solid rgba(245, 158, 11, 0.5)',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Options Pro Status */}
            {optionsProSubscription?.active && (
              <div style={{
                background: 'rgba(16, 185, 129, 0.2)',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Check size={18} color="#10b981" />
                <span style={{ color: '#10b981', fontWeight: '600' }}>
                  Options Pro Active
                </span>
                <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: '0.875rem' }}>
                  {optionsProSubscription.isTrialing 
                    ? `Trial ends ${new Date(optionsProSubscription.currentPeriodEnd).toLocaleDateString()}`
                    : `Renews ${new Date(optionsProSubscription.currentPeriodEnd).toLocaleDateString()}`
                  }
                </span>
              </div>
            )}

            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth < 768 ? '1fr' : '1fr 1fr',
              gap: '2rem',
              alignItems: 'start'
            }}>
              {/* Left: Features */}
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#f59e0b' }}>
                  🎯 Included Features
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {optionsProPlan.features.map((feature, idx) => (
                    <li key={idx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.75rem',
                      color: '#e2e8f0',
                      fontSize: '0.95rem'
                    }}>
                      <Check size={16} color="#f59e0b" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Right: Pricing & CTA */}
              <div style={{
                background: 'rgba(30, 41, 59, 0.5)',
                borderRadius: '0.75rem',
                padding: '1.5rem'
              }}>
                {/* Billing Toggle */}
                <div style={{
                  display: 'flex',
                  background: 'rgba(15, 23, 42, 0.5)',
                  borderRadius: '0.5rem',
                  padding: '0.25rem',
                  marginBottom: '1.5rem'
                }}>
                  <button
                    onClick={() => setOptionsBillingCycle('monthly')}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '0.375rem',
                      border: 'none',
                      background: optionsBillingCycle === 'monthly' ? '#f59e0b' : 'transparent',
                      color: optionsBillingCycle === 'monthly' ? 'white' : '#94a3b8',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setOptionsBillingCycle('yearly')}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '0.375rem',
                      border: 'none',
                      background: optionsBillingCycle === 'yearly' ? '#f59e0b' : 'transparent',
                      color: optionsBillingCycle === 'yearly' ? 'white' : '#94a3b8',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}
                  >
                    Yearly
                  </button>
                </div>

                {/* Price */}
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '0.25rem' }}>
                    <span style={{ fontSize: '3rem', fontWeight: '700', color: '#f59e0b' }}>
                      ${optionsBillingCycle === 'monthly' 
                        ? optionsProPlan.price.monthly 
                        : (optionsProPlan.price.yearly / 12).toFixed(0)
                      }
                    </span>
                    <span style={{ color: '#94a3b8', fontSize: '1.25rem' }}>/month</span>
                  </div>
                  {optionsBillingCycle === 'yearly' && (
                    <>
                      <p style={{ color: '#10b981', fontSize: '0.875rem', margin: '0.25rem 0' }}>
                        ${optionsProPlan.price.yearly} billed annually
                      </p>
                      <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: 0 }}>
                        Save ${(optionsProPlan.price.monthly * 12 - optionsProPlan.price.yearly).toFixed(0)}/year
                      </p>
                    </>
                  )}
                </div>

                {/* CTA */}
                <button
                  onClick={() => handleSubscribe(optionsProPlan, true)}
                  disabled={optionsProSubscription?.active || checkoutLoading === 'options_pro'}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    borderRadius: '0.5rem',
                    border: 'none',
                    background: optionsProSubscription?.active
                      ? '#475569'
                      : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: optionsProSubscription?.active ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  {checkoutLoading === 'options_pro' ? (
                    <>
                      <Loader size={20} className="spin" />
                      Processing...
                    </>
                  ) : optionsProSubscription?.active ? (
                    <>
                      <Check size={20} />
                      Currently Subscribed
                    </>
                  ) : (
                    <>
                      <Target size={20} />
                      Start 7-Day Free Trial
                    </>
                  )}
                </button>

                {optionsProSubscription?.active && (
                  <button
                    onClick={handleManageSubscription}
                    style={{
                      width: '100%',
                      marginTop: '0.75rem',
                      padding: '0.75rem',
                      background: 'transparent',
                      border: '1px solid #f59e0b',
                      borderRadius: '0.5rem',
                      color: '#f59e0b',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '0.875rem'
                    }}
                  >
                    Manage Options Subscription
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Trust Badges */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '2rem',
        flexWrap: 'wrap',
        padding: '1.5rem',
        background: 'rgba(30, 41, 59, 0.3)',
        borderRadius: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8' }}>
          <Shield size={20} />
          <span style={{ fontSize: '0.875rem' }}>Secure Payment</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8' }}>
          <Clock size={20} />
          <span style={{ fontSize: '0.875rem' }}>7-Day Free Trial</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8' }}>
          <CreditCard size={20} />
          <span style={{ fontSize: '0.875rem' }}>Cancel Anytime</span>
        </div>
      </div>

      {/* FAQ Section */}
      <div style={{ marginTop: '3rem' }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          textAlign: 'center',
          marginBottom: '1.5rem'
        }}>
          Frequently Asked Questions
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
          gap: '1rem'
        }}>
          <FAQItem
            question="How does the 7-day free trial work?"
            answer="Start your trial with full access to all features. You won't be charged until the trial ends. Cancel anytime before the trial ends to avoid charges."
          />
          <FAQItem
            question="Can I have both subscriptions?"
            answer="Yes! Options Pro is a separate subscription that can be added to any plan (Free, Pro, or Premium). You'll be billed separately for each."
          />
          <FAQItem
            question="What payment methods do you accept?"
            answer="We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe."
          />
          <FAQItem
            question="How do I cancel my subscription?"
            answer="You can cancel anytime from your account settings or the billing portal. You'll continue to have access until the end of your billing period."
          />
        </div>
      </div>
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{
      background: 'rgba(30, 41, 59, 0.5)',
      border: '1px solid #334155',
      borderRadius: '0.5rem',
      overflow: 'hidden'
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '1rem',
          background: 'transparent',
          border: 'none',
          color: 'white',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.95rem',
          fontWeight: '600'
        }}
      >
        {question}
        <span style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div style={{
          padding: '0 1rem 1rem',
          color: '#94a3b8',
          fontSize: '0.875rem',
          lineHeight: '1.5'
        }}>
          {answer}
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;